import React, { FunctionComponent, useState } from 'react';
import { Hint } from 'react-vis';
import { format } from 'd3-format';
import { propEq, filter, pathOr } from 'ramda';
import withStyles from 'react-jss';

import { ClassProp } from 'helpers/types';

const styles = {
  hint: {
    background: 'rgba(0,0,0,.6)',
    color: '#fff',
    padding: '1em',
    borderRadius: '0.25em',
    fontSize: '0.75em !important',

    '& *': {
      color: '#fff !important',
    },
  },
};

export type HintArgs = {
  onValueMouseOver: Function;
  onValueMouseOut: Function;
  renderHint: Function;
};

type Props = {
  children: (args: HintArgs) => any;
  classes: ClassProp<typeof styles>;
  data: any;
};

type HintValue = {
  x: string;
  y: number;
};

type State = {
  hintValue: HintValue;
  isMouseOver: boolean;
  type: string;
  formatType: string;
};

const getFormattedValue = (type: string, value: number) =>
  format(type)(type.indexOf('%') >= 0 ? value / 100 : value);

const WithHint: FunctionComponent<Props> = ({ data, classes, children }) => {
  const [state, setState] = useState<State>({
    isMouseOver: false,
    hintValue: { x: '01-Jan', y: 0 },
    type: '',
    formatType: '$,.0f',
  });

  const onValueMouseOver = (type: string, formatType = '$,.0f') => (
    hintValue: HintValue,
  ) => {
    setState({ formatType, type, hintValue, isMouseOver: true });
  };

  const onValueMouseOut = (hintValue: HintValue) => {
    setState({ hintValue, isMouseOver: false, type: '', formatType: '$,.0f' });
  };

  const renderHint = () => {
    const { isMouseOver, hintValue, type, formatType } = state;

    if (isMouseOver) {
      const getAssociatedValue = filter(propEq('x', hintValue.x));
      const getValue = pathOr(
        hintValue.y,
        [0, 'y'],
        getAssociatedValue(data[type]),
      );

      return (
        <Hint value={hintValue}>
          <div className={classes.hint}>
            <p>x: {hintValue.x}</p>
            <p>
              <span>y: {getFormattedValue(formatType, getValue)}</span>
            </p>
          </div>
        </Hint>
      );
    }

    return null;
  };

  return children({ onValueMouseOver, onValueMouseOut, renderHint });
};

export default withStyles(styles)(WithHint);
